import { SOURCE_KINDS, type SourceKind } from '@hr/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArchiveIcon, ArchiveRestoreIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { PageHeader } from '@/components/page';
import { AddForm, RefList, RefRow } from '@/components/ref-list';
import { DocumentTypesTab, TemplatesTab } from '@/features/personnel/reference-tabs';
import { CentersTab, DonorsTab, ServicesTab } from '@/features/registry/reference-tabs';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DefaultStagesPanel } from '@/features/contests/stage-editor';
import { EmailTemplatesPanel } from '@/features/mail/email-templates';
import { MailSettingsPanel } from '@/features/mail/mail-settings';
import { ReminderSettingsPanel } from '@/features/reminders/reminder-settings';
import { SOURCE_KIND_LABELS, uk } from '@/i18n/uk';
import { useReferences } from '@/lib/references';
import { errorMessage, trpc } from '@/lib/trpc';
import { isHr } from '@/lib/roles';

const TABS = [
  'positions',
  'sources',
  'locations',
  'stages',
  'reminders',
  'donors',
  'centers',
  'services',
  'documents',
  'templates',
  'emailTemplates',
  'mail',
] as const;

export const Route = createFileRoute('/_app/reference')({
  validateSearch: z.object({ tab: z.enum(TABS).optional().catch(undefined) }),
  component: ReferencePage,
});

function ReferencePage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { me } = Route.useRouteContext();
  const isAdmin = me.role === 'admin';
  const canAddPosition = isHr(me.role);
  const refs = useReferences();
  const qc = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);

  const opts = {
    onError: (e: unknown) => toast.error(errorMessage(e)),
    onSuccess: () => toast.success(uk.common.saved),
    onSettled: () => qc.invalidateQueries(trpc.reference.pathFilter()),
  };
  const createPosition = useMutation(trpc.reference.createPosition.mutationOptions(opts));
  const updatePosition = useMutation(trpc.reference.updatePosition.mutationOptions(opts));
  const createSource = useMutation(trpc.reference.createSource.mutationOptions(opts));
  const updateSource = useMutation(trpc.reference.updateSource.mutationOptions(opts));
  const createLocation = useMutation(trpc.reference.createLocation.mutationOptions(opts));
  const updateLocation = useMutation(trpc.reference.updateLocation.mutationOptions(opts));

  if (refs.isPending) return <Skeleton className="h-64" />;
  const data = refs.data!;

  return (
    <>
      <PageHeader title={uk.reference.title} description={!isAdmin && uk.reference.adminOnly} />
      <Tabs
        value={search.tab ?? 'positions'}
        onValueChange={(v) =>
          void navigate({
            search: { tab: v === 'positions' ? undefined : (v as (typeof TABS)[number]) },
          })
        }
      >
        <TabsList>
          <TabsTrigger value="positions">{uk.reference.positions}</TabsTrigger>
          <TabsTrigger value="sources">{uk.reference.sources}</TabsTrigger>
          <TabsTrigger value="locations">{uk.reference.locations}</TabsTrigger>
          <TabsTrigger value="stages">{uk.stages.defaultTitle}</TabsTrigger>
          <TabsTrigger value="reminders">{uk.reminders.settingsTitle}</TabsTrigger>
          <TabsTrigger value="donors">{uk.registry.donors}</TabsTrigger>
          <TabsTrigger value="centers">{uk.registry.centers}</TabsTrigger>
          <TabsTrigger value="services">{uk.registry.servicesCatalog}</TabsTrigger>
          <TabsTrigger value="documents">{uk.personnel.documentTypes}</TabsTrigger>
          <TabsTrigger value="templates">{uk.personnel.templates}</TabsTrigger>
          <TabsTrigger value="emailTemplates">{uk.emailTemplates.tab}</TabsTrigger>
          {isAdmin && <TabsTrigger value="mail">{uk.mailSettings.tab}</TabsTrigger>}
        </TabsList>

        <TabsContent value="positions" className="max-w-2xl space-y-3 pt-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            {canAddPosition && <AddForm onAdd={(title) => createPosition.mutateAsync({ title })} />}
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={showArchived}
                onCheckedChange={(v) => setShowArchived(v === true)}
              />
              {uk.reference.showArchived}
            </label>
          </div>
          <RefList>
            {data.positions
              .filter((p) => showArchived || !p.isArchived)
              .map((p) => (
                <RefRow
                  key={p.id}
                  name={p.title}
                  muted={p.isArchived}
                  badge={p.isArchived ? uk.reference.archived : undefined}
                  canEdit={isAdmin}
                  onRename={(title) => updatePosition.mutateAsync({ id: p.id, title })}
                  extra={
                    isAdmin && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        aria-label={p.isArchived ? uk.common.unarchive : uk.common.archive}
                        title={p.isArchived ? uk.common.unarchive : uk.common.archive}
                        onClick={() =>
                          updatePosition.mutate({ id: p.id, isArchived: !p.isArchived })
                        }
                      >
                        {p.isArchived ? <ArchiveRestoreIcon /> : <ArchiveIcon />}
                      </Button>
                    )
                  }
                />
              ))}
          </RefList>
        </TabsContent>

        <TabsContent value="sources" className="max-w-2xl space-y-3 pt-2">
          {isAdmin && (
            <AddForm onAdd={(name) => createSource.mutateAsync({ name, kind: 'other' })} />
          )}
          <RefList>
            {data.sources.map((s) => (
              <RefRow
                key={s.id}
                name={s.name}
                canEdit={isAdmin}
                onRename={(name) => updateSource.mutateAsync({ id: s.id, name })}
                extra={
                  <Select
                    value={s.kind}
                    disabled={!isAdmin}
                    onValueChange={(kind) =>
                      updateSource.mutate({ id: s.id, kind: kind as SourceKind })
                    }
                  >
                    <SelectTrigger size="sm" className="w-40" aria-label={uk.reference.kind}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SOURCE_KINDS.map((k) => (
                        <SelectItem key={k} value={k}>
                          {SOURCE_KIND_LABELS[k]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                }
              />
            ))}
          </RefList>
        </TabsContent>

        <TabsContent value="locations" className="max-w-2xl space-y-3 pt-2">
          {isAdmin && <AddForm onAdd={(name) => createLocation.mutateAsync({ name })} />}
          <RefList>
            {data.locations.map((l) => (
              <RefRow
                key={l.id}
                name={l.name}
                canEdit={isAdmin}
                onRename={(name) => updateLocation.mutateAsync({ id: l.id, name })}
              />
            ))}
          </RefList>
        </TabsContent>
        <TabsContent value="stages" className="max-w-3xl pt-2">
          <DefaultStagesPanel canWrite={canAddPosition} />
        </TabsContent>
        <TabsContent value="reminders" className="max-w-3xl pt-2">
          <ReminderSettingsPanel canWrite={canAddPosition} />
        </TabsContent>
        <TabsContent value="donors" className="max-w-2xl space-y-3 pt-2">
          <DonorsTab canWrite={canAddPosition} />
        </TabsContent>
        <TabsContent value="centers" className="max-w-4xl space-y-3 pt-2">
          <CentersTab canWrite={canAddPosition} />
        </TabsContent>
        <TabsContent value="services" className="max-w-4xl space-y-3 pt-2">
          <ServicesTab canWrite={canAddPosition} />
        </TabsContent>
        <TabsContent value="documents" className="max-w-4xl space-y-3 pt-2">
          <DocumentTypesTab isAdmin={isAdmin} />
        </TabsContent>
        <TabsContent value="templates" className="max-w-4xl pt-2">
          <TemplatesTab isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="emailTemplates" className="max-w-4xl pt-2">
          <EmailTemplatesPanel canWrite={isHr(me.role)} />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="mail" className="max-w-3xl pt-2">
            <MailSettingsPanel myEmail={me.email} />
          </TabsContent>
        )}
      </Tabs>
    </>
  );
}
